import * as React from "react";
import PlanTable from "../components/PlanTable";
import { fetchWindsRegionText, parseStationLine } from "../components/windsAloft";
import ForecastTerminalTable from "../components/ForecastTerminalTable";
import NavlogWeatherLinks from "../components/NavlogWeatherLinks";
import NavlogTotals from "../components/NavlogTotals";
import {
  winds,
  metar,
  taf,
  afd,
  mvar,
} from "../data/weatherLinks";
import NavlogWindsControls from "../components/NavlogWindsControls";
import { computeNavlogLegs } from "../utils/computeNavlogLegs";
import {
  computeTotalGalsUsed,
  computeTotalEteMin,
  formatTime,
  computeTotalDistance,
  computeFuelRemaining,
  computePressureAltitudeFt,
  computeDensityAltitudeFt,
  fmtFt0,
} from "../utils/navlogTotals";
import NavlogDepartureSettings from "../components/NavlogDepartureSettings";
import NavlogSaveLoadControls from "../helpers/NavlogSaveLoadControls";
import NavlogPressureAltitude from "../components/NavlogPressureAltitude";
import MetarTaf from "../components/MetarTaf.jsx";
import  
 {
  metarsLink,
  tafsLink,
  adipFaaLink,
  terminalLink,
  notamsLink,
  windsLink,
  
} from "../data/planningLinks.js";
import { Link } from 'react-router-dom';


const NavLogPage = ({currentAircraft}) => {
  
  // ------------------------------------------------------------
  // STATE HOOKS declaration
  // ------------------------------------------------------------
  // Default values used when creating row
  const [defaultRegion] = React.useState("mia");
  const [defaultFt] = React.useState("");
  const [defaultCheckpoint] = React.useState("EAONE");
  const [defaultCruiseAlt] = React.useState("7000");
  const [defaultCas] = React.useState("130");
  const [defaultTc] = React.useState("180");
  const [defaultNm] = React.useState("15");
  const [defaultGph] = React.useState("10.0");
  const [defaultMdev] = React.useState("6");
  const [defaultCdev] = React.useState("1.1");
  const [windsRegion, setWindsRegion] = React.useState("mia");
  const [windsFcst, setWindsFcst] = React.useState("6");
  const [stationListsByRegion, setStationListsByRegion] = React.useState({});

  
  // Nav log input rows (editable in the table)
  const [navlogRows, setNavlogRows] = React.useState([
  { ft: "ATL", checkpoint: "Top of Climb", cruiseAlt: "3000", cas: "103", tc: "182", nm: "9.2", gph: "15", mdev: "6", cdev: "1.1" },
  { ft: "ATL", checkpoint: "EAONE", cruiseAlt: "7000", cas: "130", tc: "182", nm: "15.0", gph: "10.0", mdev: "6", cdev: "1.1" },
  { ft: "ATL", checkpoint: "AMG", cruiseAlt: "7000", cas: "130", tc: "161", nm: "154", gph: "10.0", mdev: "6", cdev: "1.1" },
  { ft: "ATL", checkpoint: "OTK", cruiseAlt: "7000", cas: "130", tc: "228", nm: "61", gph: "10.0", mdev: "6", cdev: "1.1"  },
  { ft: "PIE", checkpoint: "OCF", cruiseAlt: "6000", cas: "130", tc: "156", nm: "100", gph: "10.0", mdev: "6", cdev: "1.1" },
  { ft: "PIE", checkpoint: "ALADN", cruiseAlt: "6000", cas: "140", tc: "135", nm: "11", gph: "8.0", mdev: "6", cdev: "1.1" },
  { ft: "PIE", checkpoint: "SHIMM", cruiseAlt: "4000", cas: "140", tc: "135", nm: "9.0", gph: "8.0", mdev: "6", cdev: "1.1" },
  { ft: "PIE", checkpoint: "LEESE", cruiseAlt: "4000", cas: "120", tc: "136", nm: "10", gph: "8.0", mdev: "6", cdev: "1.1" },
  { ft: "PIE", checkpoint: "KORL", cruiseAlt: "4000", cas: "120", tc: "135", nm: "31", gph: "8.0", mdev: "6", cdev: "1.1" },
]);

  // Departure / local conditions (persisted)
  const [departElevation, setDepartElevation] = React.useState("1061");
  const [localAltimeter, setLocalAltimeter] = React.useState("29.92"); // default to standard pressure
  const [localOat, setLocalOat] = React.useState("18");

  const [forecastTerminals, setForecastTerminals] = React.useState([
  {
    region: "Southeast",
    ft: "ATL",
    alt1: "3000",
    alt2: "6000",
    alt3: "9000",
    wd1: "250",
    wd2: "260",
    wd3: "270",
    ws1: "",
    ws2: "",
    ws3: "",
    t1: "",
    t2: "",
    t3: "",
  },
  {
    region: "Southeast",
    ft: "PIE",
    alt1: "3000",
    alt2: "6000",
    alt3: "9000",
    wd1: "240",
    wd2: "250",
    wd3: "260",
    ws1: "",
    ws2: "",
    ws3: "",
    t1: "",
    t2: "",
    t3: "",
  },
]);

  const [startFuel, setStartFuel] = React.useState("48");
  const airplaneId = currentAircraft?.tailNumber || "";
  const [windDirection, setWindDirection] = React.useState("0");
  const [windSpeed, setWindSpeed] = React.useState("0");
  const [runwayHeading, setRunwayHeading] = React.useState("0");

  // ------------------------------------------------------------
  // ROW HELPERS
  // ------------------------------------------------------------

  /**
   * Anonymous function for a button onClick handler
   * 
   * 'prev' is the previous state.  the spread operator '...' 
   * is used to create a new array that includes all the previous 
   * rows plus a new row with default values.
   * React uses this to add a new item to an array in state, 
   * because states should not be mutated directly.
   *  
   * https://react.dev/reference/react/useState#updating-state-based-on-the-previous-state
   */
  const addNavlogRow = () => {
    setNavlogRows((prev) => [ // prev is the previous state
      ...prev, //spread operator to include all previous rows. it makes a copy of the prev array.
     {
        region: defaultRegion,
        ft: defaultFt,
        checkpoint: defaultCheckpoint,
        cruiseAlt: defaultCruiseAlt,
        cas: defaultCas,
        tc: defaultTc,
        nm: defaultNm,
        gph: defaultGph,
        mdev: defaultMdev,
        cdev: defaultCdev,
      },
    ]);
  };


  /**
   * ---------------------------------------------------------------
   * delete one row from the navlog table
   * ---------------------------------------------------------------
   * 
   * This function removes a specific row from the navlog table.
   *
   * Parameter:
   *   i = index of the row to delete
   * 
   * Operation:
   * 1. setNavlogRows receives an anonymous callback function.
   * 2. React passes the previous state array into that callback as prev.
   * 3. The function checks if the length of prev is 1 or less. If so, it returns prev without changes, preventing deletion of the last remaining row.
   * 4. the filter() method creates a new array excluding the row at index i
   * 5. The updated array is returned so React can store the new state 
   * 6. React re-renders the table without the deleted row.
   * 
   */
  const deleteNavlogRow = (i) => {
    setNavlogRows((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, idx) => idx !== i);
    });
  };

  /**
   * use anonymous function to update a specific cell in the navlog table.
   * 'i' is the index of the row to update
   * 'field' is the name of the field property to update
   * 'value' is the new value for the field
   * 
   * Operation:
   * 1. setNavlogRows receives an anonymous callback function.
   * 2. React passes the previous state array into that callback as prev.
   * 3. A new array named copy is created using the spread operator (...prev).
   *    This prevents direct modification of the original state array.
   * 4. The specific row at index i is replaced with a new object.
   * 5. That new object keeps all existing values from copy[i],
   *    then overwrites only the selected field with the new value.
   * 6. The updated array is returned so React can store the new state
 *    and re-render the table.
   * 
   * This approach ensures that state updates are predictable and that React can efficiently
   * determine what has changed and update the DOM accordingly.
   * 
   * Example:
   *   updateNavlogCell(2, "checkpoint", "ATL")
   * 
   * Result:
   *  - the checkpoint field in the row at index 2 will be updated to "ATL"
   * 
   *
  */
  const updateNavlogCell = (i, field, value) => {
    setNavlogRows((prev) => {
      const copy = [...prev]; // spread operator to include all previous rows.
      copy[i] = { ...copy[i], [field]: value };
      return copy;
    });
  };

  // ------------------------------------------------------------
  // HELPER for Forecast Terminals
  // ------------------------------------------------------------
  /**
   * Updates a specific cell in the forecast terminals table.
 *
 * Parameters:
 *  - i: index of the row to update
 *  - field: name of the property to update
 *  - value: new value for that property
 *
 * Operation:
 * 1. setForecastTerminals is called with a callback function.
 * 2. React provides the previous state (prev) to that callback.
 * 3. A shallow copy of the array is created using the spread operator:
 *      const copy = [...prev];
 *    This avoids mutating the original state.
 * 4. The row at index i is replaced with a new object:
 *      copy[i] = { ...copy[i], [field]: value };
 *    - All existing properties are preserved
 *    - Only the specified field is updated
 * 5. The updated array is returned, allowing React to:
 *    - store the new state
 *    - trigger a re-render
 *
 * Why this approach:
 *  - Maintains immutability (required for reliable React state updates)
 *  - Ensures predictable rendering behavior
 *  - Allows React to efficiently detect changes
 *
 * Example:
 *   updateForecastTerminalCell(2, "checkpoint", "ATL");
 *
 * Result:
 * - The checkpoint field in the forecast terminal row at index 2 will be updated to "ATL"
   *
  */
    const updateForecastTerminalCell = (i, field, value) => {
       setForecastTerminals((prev) => {
          const copy = [...prev];  // create shallow copy of state array
          copy[i] = { ...copy[i], [field]: value };  // update specific field
          return copy;
  });
};

  // ------------------------------------------------------------
  // LOAD WINDS ALOFT FOR ALL ROWS
  // async causes the function to return a promise,
  // await is used to wait for the promise to resolve before continuing
  // this is middleware between NavlogWindsControls and fetch/parse functions in windsAloft.js
  // ------------------------------------------------------------
  const loadAllWinds = async () => {
    const regionTextCache = {};

  setForecastTerminals((prev) => {
    return prev.map((row) => row);
  });
const updatedRows = await Promise.all(
    forecastTerminals.map(async (row) => {
      const rowRegion = row.region || "mia";

      if (!regionTextCache[rowRegion]) {
        const result = await fetchWindsRegionText(rowRegion, windsFcst);
        regionTextCache[rowRegion] = result?.rawText || "";
      }

      const rawText = regionTextCache[rowRegion];

      if (!rawText) return row;

      const parsed = parseStationLine(rawText, row.ft);

      if (!parsed) return row;

      return {
        ...row,
        region: rowRegion,  // update region to match the winds region
        alt1: parsed.alt1,
        alt2: parsed.alt2,
        alt3: parsed.alt3,
        wd1: parsed.wd1,
        ws1: parsed.ws1,
        t1: parsed.t1,
        wd2: parsed.wd2,
        ws2: parsed.ws2,
        t2: parsed.t2,
        wd3: parsed.wd3,
        ws3: parsed.ws3,
        t3: parsed.t3,
      };
    })
  );
  setForecastTerminals(updatedRows);
};

  /**
   * *****************************************
   * PERSIST departure and wind values
   * 'navlog_globals_v1' is the key value used in LocalStorage
   * to store the global variables as a JSON string.  
   * When the component mounts, it retrieves the saved values 
   * and updates the state accordingly.
   * 
   * 'g' is an object that contains all the global variables
   * ***************************************
   */
  React.useEffect(() => {
    const saved = localStorage.getItem("navlog_globals_v1");
    if (!saved) return;

    try {
      const g = JSON.parse(saved); // parse the JSON string back into an object
      
      // check for null then set state for each global variable
      // departure / local conditions
      if (g.departElevation != null) setDepartElevation(String(g.departElevation));
      if (g.localAltimeter != null) setLocalAltimeter(String(g.localAltimeter));
      if (g.localOat != null) setLocalOat(String(g.localOat));
      if (g.startFuel != null) setStartFuel(String(g.startFuel));
      if (g.windsRegion != null) setWindsRegion(String(g.windsRegion));
      if (g.windsFcst != null) setWindsFcst(String(g.windsFcst));

      if (Array.isArray(g.forecastTerminals) && g.forecastTerminals.length === 2) {
          setForecastTerminals(g.forecastTerminals);

      if (Array.isArray(g.navlogRows) && g.navlogRows.length > 0) { 
        setNavlogRows(g.navlogRows); 
      } 
            }
            } catch (e) {
              // ignore corrupt or invalid JSON
            }
          }, []); // use empty dependency array [] to run only on mount

        // save to LocalStorage whenever any of the global variables change
        React.useEffect(() => {
          const g = {
            departElevation,
            localAltimeter,
            localOat,
            startFuel,
            forecastTerminals,
            navlogRows,
            windsRegion,
            windsFcst,
          };
          // 'g' is an object that contains all the global variables
          // JSON.stringify converts the 'g' object into a string
          localStorage.setItem("navlog_globals_v1", JSON.stringify(g));
          // list all the global variables in the dependency array
          // so that the effect runs whenever any of them change
        }, [
          departElevation,
          localAltimeter,
          localOat,
          startFuel,
          forecastTerminals,
          navlogRows,
          windsRegion,
          windsFcst,
        ]);
 
        // load station list for dropdown in forecast terminal table.
        React.useEffect(() => {
          async function loadStationListsByRegion() {
            const regions = ["bos", "mia", "chi", "dfw", "slc", "sfo"];
            const lists = {};

              for (const region of regions) {
                  const result = await fetchWindsRegionText(region, windsFcst);

                    if (!result?.rawText) {
                      lists[region] = [];
                      return;
                    }

              lists[region] = result.rawText
                .split("\n")
                .map((line) => {
                  const match = line.match(/^([A-Z]{3})\s/);
                  return match ? match[1] : null;
                })
                .filter(Boolean);
              }

              setStationListsByRegion(lists);
            }

            loadStationListsByRegion();
          }, [windsFcst]);

  /**
   * ------------------------------------------------------------
   * COMPUTE navlog outputs per row
   * 
   * memoize the computed navlog legs so that they are only recalculated
   * when the input values (navlogRows and forecastTerminals) change.
   * 
   * note: map creates a new array
   * for each row in the navlog, it finds the corresponding forecast terminal data
   * 
   * keep useMemo on this page because memoization belongs
   * to the React component lifecycle while calculations 
   * belong in the utils directory.
   *  ------------------------------------------------------------
   */
  
  const navlogLegsComputed = React.useMemo(() => {
      return computeNavlogLegs(navlogRows, forecastTerminals);
    }, [navlogRows, forecastTerminals]);
        

  // ------------------------------------------------------------
// TOTAL gallons used
// ------------------------------------------------------------
const totalGalsUsed = React.useMemo(() => {
   return computeTotalGalsUsed(navlogLegsComputed);
}, [navlogLegsComputed]);

// ------------------------------------------------------------
// TOTAL ETE (minutes)
// ------------------------------------------------------------
const totalEteMin = React.useMemo(() => {
 return computeTotalEteMin(navlogLegsComputed);
}, [navlogLegsComputed]);


// ------------------------------------------------------------
// TOTAL DISTANCE (nautical miles)
// ------------------------------------------------------------
const totalDistance = React.useMemo(() => {
  return computeTotalDistance(navlogLegsComputed);
}, [navlogLegsComputed]);



// ------------------------------------------------------------
// FUEL REMAINING
// ------------------------------------------------------------
const fuelRemaining = React.useMemo(() => {
  return computeFuelRemaining(
    startFuel,
    totalGalsUsed
  );
}, [startFuel, totalGalsUsed]);

// ------------------------------------------------------------
// PRESSURE ALTITUDE (PA) and DENSITY ALTITUDE (DA)
// Uses field elevation, altimeter, and OAT
// ------------------------------------------------------------
const pressureAltitudeFt = React.useMemo(() => {
  return computePressureAltitudeFt(
    departElevation,
    localAltimeter
  );
}, [departElevation, localAltimeter]);

const densityAltitudeFt = React.useMemo(() => {
    return computeDensityAltitudeFt(
        pressureAltitudeFt,
        localOat
      );
}, [pressureAltitudeFt, localOat]);

  // ------------------------------------------------------------
  // User Interface HTML
  // ------------------------------------------------------------
  return (
    <div style={{ padding: "12px" }}>
      
        <NavlogDepartureSettings
            departElevation={departElevation}
            setDepartElevation={setDepartElevation}
            localAltimeter={localAltimeter}
            setLocalAltimeter={setLocalAltimeter}
            localOat={localOat}
            setLocalOat={setLocalOat}
            startFuel={startFuel}
            setStartFuel={setStartFuel}
            windDirection={windDirection}
            setWindDirection={setWindDirection}
            windSpeed={windSpeed}
            setWindSpeed={setWindSpeed}
            runwayHeading={runwayHeading}
            setRunwayHeading={setRunwayHeading}
          />
          <NavlogPressureAltitude 
              pressureAltitudeFt={pressureAltitudeFt}
              densityAltitudeFt={densityAltitudeFt}
              fmtFt0={fmtFt0}
            />
             
             <MetarTaf
               metarsLink={metarsLink}
               tafsLink={tafsLink}
               adipFaaLink={adipFaaLink}
               terminalLink={terminalLink}
               notamsLink={notamsLink}
               windsLink={windsLink}
             />
        
      <NavlogWindsControls
          stationListsByRegion={stationListsByRegion}
          windsFcst={windsFcst}
          setWindsFcst={setWindsFcst}
          loadAllWinds={loadAllWinds}
        />

        <div className="forecast-terminal-scroll">
          <ForecastTerminalTable
              forecastTerminals={forecastTerminals}
              stationListsByRegion={stationListsByRegion}
              updateForecastTerminalCell={updateForecastTerminalCell}
            />
          </div>

        <NavlogSaveLoadControls
            selectedAirplane={{id: airplaneId }}
            navlogRows={navlogRows}
            setNavlogRows={setNavlogRows}
            forecastTerminals={forecastTerminals}
            setForecastTerminals={setForecastTerminals}
            windsRegion={windsRegion}
            setWindsRegion={setWindsRegion}
            windsFcst={windsFcst}
            setWindsFcst={setWindsFcst}
 
          />
        <div className= "navlog-wrapper">
          <PlanTable
            navlog={navlogLegsComputed}
            onEdit={updateNavlogCell}
            onAddRow={addNavlogRow}
            onDeleteRow={deleteNavlogRow}
          />
          </div>


          <NavlogTotals
              totalDistance={totalDistance}
              totalEteMin={totalEteMin}
              totalGalsUsed={totalGalsUsed}
              fuelRemaining={fuelRemaining}
              pressureAltitudeFt={pressureAltitudeFt}
              densityAltitudeFt={densityAltitudeFt}
              formatTime={formatTime}
              fmtFt0={fmtFt0}
            />

            <NavlogWeatherLinks
              winds={winds}
              metar={metar}
              taf={taf}
              afd={afd}
              mvar={mvar}
            />
{/* ============================================================
    OPTIONAL AD PLACEMENT
  ============================================================ */}
  <div className="ad-banner">
      Ad Space
  </div>
                              
  {/* ============================================================
    RELATED TOOLS / INTERNAL LINKS
    ============================================================ */}
  <div className="tool-content">
    <h2 >Related Tools</h2>
                              
    <p >
    <Link to="/">HomePage</Link> |
    <Link to="/vsps">Vspeed</Link> |
    <Link to="/rollout">Ground Roll</Link> |
    <Link to="/wtbals">Weight & Balance</Link> |
    <Link to="/density-altitude">Density Altitude</Link> |
    <Link to="/cw">Crosswind/Headwind</Link> |
    <Link to="/mnvr">Maneuvers</Link> |
    <Link to="/airframe-powerplant">Airframe & Powerplant</Link> |
    <Link to="/airplane-prebuy">Airplane Prebuy Checklist</Link> |
    <Link to="/adjustable-amortization">Aircraft Loan</Link>
    </p>
    </div>
    </div>

  );
};

export default NavLogPage;